import { useEffect, useRef, useState } from 'react'
import { saveToStorage } from '@vend/utilities'
import { $stateParams } from '../../ng-compat/imports'
import {
  useIsCalculatingSale,
  usePaymentManager,
  useSaleManager,
} from '../../ng-compat/hooks'
import { VEND_API_LOAD_SALE_CLOSE_AFTER_COMPLETE } from '../../constants'
import { RootState } from '../../state/store'
import { useAppSelector } from '../../state/hooks'
import { useLoadSale } from './useLoadSale'
import { useSaleModals } from './useSaleModals'
import { useTransitionToPaymentScreen } from './sale-payment/sale-payment-button/use-transition-to-payment-screen'

const selectSaleLocalId = (state: RootState) => state.currentSale.localId

export const useSaleInitialising = () => {
  const localId = useAppSelector(selectSaleLocalId)
  const saleManager = useSaleManager()
  const paymentManager = usePaymentManager()
  const { loadSale } = useLoadSale()
  const transitionToPaymentScreen = useTransitionToPaymentScreen()
  const isCalculating = useIsCalculatingSale()
  const [isInitialising, setIsInitialising] = useState(true)
  const hasRun = useRef(false)
  const hasCheckedLSPayRecovery = useRef(false)
  const { openLSPayRecoveryModal } = useSaleModals()

  useEffect(() => {
    if (!isInitialising || hasRun.current) {
      return
    }

    const handleInit = async () => {
      hasRun.current = true
      if ($stateParams.quoteId) {
        await loadSale($stateParams.quoteId)
      } else if ($stateParams.sale) {
        await loadSale($stateParams.sale)
      } else {
        await saleManager.init()
      }

      if ($stateParams.closeAfterComplete) {
        saveToStorage(VEND_API_LOAD_SALE_CLOSE_AFTER_COMPLETE, {
          closeAfterComplete: $stateParams.closeAfterComplete,
        })
      }

      setIsInitialising(false)
    }

    handleInit()
  }, [isInitialising, loadSale, saleManager])

  useEffect(() => {
    if (hasCheckedLSPayRecovery.current || isInitialising) {
      return
    }
    hasCheckedLSPayRecovery.current = true
    const {
      registerSalePaymentId,
    } = paymentManager.checkAndRetriveActivePayment(localId)
    if (!registerSalePaymentId) {
      return
    }

    if (!isCalculating) {
      saleManager.queueSaleCalculation()
    }
    openLSPayRecoveryModal(localId, complete => {
      complete.then(transitionToPaymentScreen)
    })
  }, [
    isCalculating,
    isInitialising,
    localId,
    openLSPayRecoveryModal,
    paymentManager,
    saleManager,
    transitionToPaymentScreen,
  ])

  return isInitialising
}
